import React, { useEffect, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LuLayoutDashboard,
  LuFileText,
  LuUsers,
  LuBox,
  LuChevronRight,
  LuChevronDown,
  LuPercent,
  LuReceipt,
  LuCreditCard,
  LuTruck,
  LuBriefcase,
  LuTags,
  LuLifeBuoy,
  LuPackage,
  LuMessageSquare,
  LuUserPlus,
  LuShoppingBag,
  LuSettings2
} from "react-icons/lu";
import { useSelector, useDispatch } from 'react-redux';
import { getStats } from '../redux/reducers/communicationSlice';

// ── Nav group component ──────────────────────────────
const NavGroup = ({ icon, label, badge, children, defaultOpen = false }) => {
  const location = useLocation();

  // Auto-open group if current path matches any child
  const childPaths = React.Children.map(children, child => child?.props?.to) || [];
  const isAnyChildActive = childPaths.some(path => path && location.pathname.startsWith(path));

  const [isOpen, setIsOpen] = useState(defaultOpen || isAnyChildActive);

  // Re-check on navigation
  useEffect(() => {
    if (isAnyChildActive) setIsOpen(true);
  }, [location.pathname]);

  return (
    <div className="nav-group">
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className={`sidebar-link rounded w-100 border-0 ${isAnyChildActive ? 'active' : ''}`}
        style={{
          background: isAnyChildActive ? 'rgba(40,176,196,0.08)' : 'transparent',
          color: isAnyChildActive ? '#28B0C4' : 'var(--st-sidebar-text)',
          textAlign: 'left',
          cursor: 'pointer',
          transition: 'all 0.15s ease'
        }}
      >
        {icon}
        <span className="flex-grow-1">{label}</span>
        {badge}
        {isOpen
          ? <LuChevronDown size={12} className="chevron-icon" />
          : <LuChevronRight size={12} className="chevron-icon" />
        }
      </button>

      {isOpen && (
        <div className="nav-group-children ps-3 mt-1">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Child nav link ───────────────────────────────────
const NavChild = ({ to, icon, label, badge }) => (
  <NavLink
    to={to}
    className={({ isActive }) => `sidebar-child-link rounded d-flex align-items-center gap-2 ${isActive ? 'active' : ''}`}
    style={({ isActive }) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '7px 10px',
      marginBottom: '2px',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: 500,
      textDecoration: 'none',
      color: isActive ? '#28B0C4' : 'var(--st-sidebar-text)',
      background: isActive ? 'rgba(40,176,196,0.08)' : 'transparent',
      borderLeft: isActive ? '2px solid #28B0C4' : '2px solid transparent',
      transition: 'all 0.15s ease',
    })}
  >
    {icon && React.cloneElement(icon, { size: 15 })}
    <span className="flex-grow-1">{label}</span>
    {badge}
  </NavLink>
);

// ── Section label ────────────────────────────────────
const SectionLabel = ({ label }) => (
  <div
    className="px-2 mt-3 mb-1"
    style={{
      fontSize: '10px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
      color: '#94a3b8',
    }}
  >
    {label}
  </div>
);

// ── Divider ──────────────────────────────────────────
const Divider = () => (
  <div className="my-2 mx-2" style={{ borderTop: '1px solid var(--st-border)', opacity: 0.15 }} />
);

// ── Main Sidebar ─────────────────────────────────────
const Sidebar = ({ closeSidebar }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { stats } = useSelector((state) => state.communication);

  useEffect(() => {
    const businessId = localStorage.getItem('activeBusinessId');
    dispatch(getStats(businessId));
  }, [dispatch, location.pathname]);

  useEffect(() => {
    if (window.innerWidth <= 991 && closeSidebar) {
      closeSidebar();
    }
  }, [location.pathname]);

  const unreadBadge = stats.unreadConversations > 0 ? (
    <span className="rounded-pill px-2 fw-bold me-1"
      style={{ fontSize: '10px', background: '#28B0C4', color: 'white', minWidth: '20px', textAlign: 'center' }}>
      {stats.unreadConversations}
    </span>
  ) : null;

  const pendingBadge = stats.pendingInvitations > 0 ? (
    <span className="rounded-pill px-2 fw-bold me-1"
      style={{ fontSize: '10px', background: '#f59e0b', color: 'white', minWidth: '20px', textAlign: 'center' }}>
      {stats.pendingInvitations}
    </span>
  ) : null;

  return (
    <div className="sidebar-container d-flex flex-column">

      {/* ── Logo ── */}
      <div className="p-4 d-flex align-items-center border-bottom" style={{ height: '70px' }}>
        <div className="d-flex align-items-center gap-2">
          <div className="rounded-2 flex-shrink-0" style={{ width: 4, height: 42, background: "#28B0C4" }} />
          <div>
            <div className="fw-bold text-dark" style={{ fontSize: "18px", lineHeight: 1.2 }}>
              Biz 360 by STS
            </div>
            <div className="text-muted" style={{ fontSize: "12px", lineHeight: 1.2 }}>
              Inventory Management System
            </div>
          </div>
        </div>
      </div>

      {/* ── Nav ── */}
      <div className="flex-grow-1 overflow-auto px-2 py-2">

        {/* Dashboard — standalone, no group */}
        <NavLink
          to="/"
          end
          className={({ isActive }) => `sidebar-link rounded ${isActive ? 'active' : ''}`}
        >
          <LuLayoutDashboard size={18} />
          <span className="flex-grow-1">Dashboard</span>
          <LuChevronRight className="chevron-icon" size={12} />
        </NavLink>

        <Divider />

        {/* ── Finance ── */}
        <SectionLabel label="Finance" />

        <NavGroup
          icon={<LuFileText size={18} />}
          label="Billings"
          defaultOpen={['/invoice', '/transaction'].some(p => location.pathname.startsWith(p))}
        >
          <NavChild to="/invoice"     icon={<LuFileText />}   label="Invoices" />
          <NavChild to="/transaction" icon={<LuCreditCard />} label="Transactions" />
        </NavGroup>

        <NavGroup
          icon={<LuShoppingBag size={18} />}
          label="Consignment"
          defaultOpen={['/orders', '/shipments'].some(p => location.pathname.startsWith(p))}
        >
          <NavChild to="/orders"    icon={<LuPackage />} label="Orders" />
          <NavChild to="/shipments" icon={<LuTruck />}   label="Shipments" />
        </NavGroup>

        <Divider />

        {/* ── Customers ── */}
        <SectionLabel label="Customers" />

        <NavGroup
          icon={<LuUsers size={18} />}
          label="Customer Hub"
          badge={unreadBadge || pendingBadge}
          defaultOpen={['/customer', '/communications', '/invitations'].some(p => location.pathname.startsWith(p))}
        >
          <NavChild to="/customer"       icon={<LuUsers />}        label="Customers" />
          <NavChild to="/communications" icon={<LuMessageSquare />} label="Communications" badge={unreadBadge} />
          <NavChild to="/invitations"    icon={<LuUserPlus />}      label="Invitations"    badge={pendingBadge} />
        </NavGroup>

        <Divider />

        {/* ── Catalogue ── */}
        <SectionLabel label="Catalogue" />

        <NavGroup
          icon={<LuBox size={18} />}
          label="Products"
          defaultOpen={['/product'].some(p => location.pathname.startsWith(p))}
        >
          <NavChild to="/product"          icon={<LuBox />}  label="All Products" />
          <NavChild to="/product-category" icon={<LuTags />} label="Categories" />
        </NavGroup>

        <Divider />

        {/* ── Settings ── */}
        <SectionLabel label="Settings" />

        <NavGroup
          icon={<LuSettings2 size={18} />}
          label="Configuration"
          defaultOpen={['/tax', '/discount', '/business'].some(p => location.pathname.startsWith(p))}
        >
          {/* <NavChild to="/tax"      icon={<LuReceipt />}   label="Taxes" /> */}
          <NavChild to="/discount" icon={<LuPercent />}   label="Discounts" />
          <NavChild to="/business" icon={<LuBriefcase />} label="Business" />
        </NavGroup>

        <NavLink
          to="/support"
          className={({ isActive }) => `sidebar-link rounded mt-1 ${isActive ? 'active' : ''}`}
        >
          <LuLifeBuoy size={18} />
          <span className="flex-grow-1">Support</span>
          <LuChevronRight className="chevron-icon" size={12} />
        </NavLink>

      </div>

      {/* ── Footer ── */}
      <div className="p-3 border-top">
        <div className="text-muted" style={{ fontSize: '11px' }}>© 2026 Seven Tech Solutions</div>
      </div>

    </div>
  );
};

export default Sidebar;